import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  People as PeopleIcon,
  Storage as StorageIcon,
  CreditCard as CreditCardIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useInstitution } from '../../context/InstitutionContext';
import { useAuth } from '../../context/AuthContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function InstitutionAdminDashboard() {
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const { currentInstitution, loading, error, fetchBranches, fetchClasses } = useInstitution();
  
  const [tabValue, setTabValue] = useState(0);
  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [branchLoading, setBranchLoading] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (currentUser && !['admin', 'principal'].includes(userRole)) {
      navigate('/');
    }
  }, [currentUser, userRole, navigate]);

  // Fetch branches and classes
  useEffect(() => {
    if (!currentInstitution?.id) return;

    const loadBranchesAndClasses = async () => {
      setBranchLoading(true);
      try {
        if (currentInstitution.type === 'college') {
          const branchData = await fetchBranches(currentInstitution.id);
          setBranches(branchData);
        } else {
          const classData = await fetchClasses(currentInstitution.id);
          setClasses(classData);
        }
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setBranchLoading(false);
      }
    };

    loadBranchesAndClasses();
  }, [currentInstitution?.id, currentInstitution?.type, fetchBranches, fetchClasses]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!currentInstitution) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Institution not found. Please register first.</Alert>
      </Container>
    );
  }

  const storagePercent = currentInstitution.max_storage_gb ? (currentInstitution.current_storage_gb / currentInstitution.max_storage_gb) * 100 : 0;
  const usersPercent = currentInstitution.max_users ? (currentInstitution.current_users / currentInstitution.max_users) * 100 : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {currentInstitution.name}
        </Typography>
        <Typography variant="body2" sx={{ color: '#666' }}>
          {currentInstitution.type.toUpperCase()} • {currentInstitution.city}, {currentInstitution.state}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Users Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Active Users
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {currentInstitution.current_users || 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    of {currentInstitution.max_users || 100}
                  </Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 40, color: '#2196F3', opacity: 0.5 }} />
              </Box>
              <Box sx={{ mt: 2, height: 4, bgcolor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.min(usersPercent, 100)}%`,
                    bgcolor: '#2196F3'
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                {usersPercent.toFixed(1)}% used
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Storage Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Storage
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {(currentInstitution.current_storage_gb || 0).toFixed(1)} GB
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    of {currentInstitution.max_storage_gb || 50} GB
                  </Typography>
                </Box>
                <StorageIcon sx={{ fontSize: 40, color: '#4CAF50', opacity: 0.5 }} />
              </Box>
              <Box sx={{ mt: 2, height: 4, bgcolor: '#e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.min(storagePercent, 100)}%`,
                    bgcolor: '#4CAF50'
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                {storagePercent.toFixed(1)}% used
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Plan Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Plan
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {currentInstitution.plan?.toUpperCase() || 'TRIAL'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    {currentInstitution.subscription_status}
                  </Typography>
                </Box>
                <CreditCardIcon sx={{ fontSize: 40, color: '#FF9800', opacity: 0.5 }} />
              </Box>
              <Button
                variant="text"
                size="small"
                sx={{ mt: 2 }}
                onClick={() => setTabValue(3)}
              >
                Manage Plan →
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Settings Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    Academic Year
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1 }}>
                    {currentInstitution.academic_year}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>
                    {currentInstitution.type === 'college' ? 'Semesters' : 'Classes'}
                  </Typography>
                </Box>
                <SettingsIcon sx={{ fontSize: 40, color: '#9C27B0', opacity: 0.5 }} />
              </Box>
              <Button
                variant="text"
                size="small"
                sx={{ mt: 2 }}
                onClick={() => setTabValue(3)}
              >
                Settings →
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={currentInstitution.type === 'college' ? 'Branches' : 'Classes'} />
          <Tab label="Users" />
          <Tab label="Subjects" />
          <Tab label="Settings & Billing" />
        </Tabs>

        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          {branchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  {currentInstitution.type === 'college' ? 'Branches' : 'Classes'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/add-${currentInstitution.type === 'college' ? 'branch' : 'class'}`)}
                >
                  Add {currentInstitution.type === 'college' ? 'Branch' : 'Class'}
                </Button>
              </Box>

              {currentInstitution.type === 'college' ? (
                <Grid container spacing={2}>
                  {branches.length > 0 ? (
                    branches.map((branch) => (
                      <Grid item xs={12} sm={6} md={4} key={branch.id}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6">{branch.name}</Typography>
                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                              Code: {branch.code}
                            </Typography>
                            <Chip
                              label={branch.status}
                              size="small"
                              color={branch.status === 'active' ? 'success' : 'default'}
                              sx={{ mb: 2 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" startIcon={<EditIcon />}>
                                Edit
                              </Button>
                              <Button size="small" startIcon={<DeleteIcon />} color="error">
                                Delete
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Alert>No branches added yet. Create your first branch to get started!</Alert>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Grid container spacing={2}>
                  {classes.length > 0 ? (
                    classes.map((cls) => (
                      <Grid item xs={12} sm={6} md={4} key={cls.id}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6">{cls.name}</Typography>
                            <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                              Section: {cls.section}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                              Students: {cls.current_students || 0}
                            </Typography>
                            <Chip
                              label={cls.status}
                              size="small"
                              color={cls.status === 'active' ? 'success' : 'default'}
                              sx={{ mb: 2 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button size="small" startIcon={<EditIcon />}>
                                Edit
                              </Button>
                              <Button size="small" startIcon={<DeleteIcon />} color="error">
                                Delete
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Alert>No classes added yet. Create your first class to get started!</Alert>
                    </Grid>
                  )}
                </Grid>
              )}
            </>
          )}
        </TabPanel>

        {/* Users Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Manage Users & Teachers
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/manage-users')}
              >
                View All
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/add-user')}
              >
                Add User
              </Button>
            </Box>
          </Box>
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="body2">
              👉 <strong>Quick Access:</strong> Click "View All" to see all users, edit details, or remove users. Click "Add User" to create new teacher/staff accounts.
            </Typography>
          </Box>
        </TabPanel>

        {/* Subjects Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Manage Subjects
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/subject-management')}
            >
              Manage Subjects
            </Button>
          </Box>
          <Box sx={{ mb: 3, p: 2, backgroundColor: '#f3e5f5', borderRadius: 1 }}>
            <Typography variant="body2">
              👉 <strong>Subject Management:</strong> Click the button above to add, edit, or delete subjects. Assign subjects to branches/classes and configure mark distribution.
            </Typography>
          </Box>
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>Institution Settings</Typography>
              <Paper sx={{ p: 2 }}>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Institution Name"
                      secondary={currentInstitution.name}
                    />
                    <Button size="small">Edit</Button>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Academic Year"
                      secondary={currentInstitution.academic_year}
                    />
                    <Button size="small">Edit</Button>
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Status"
                      secondary={currentInstitution.status}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ mb: 2 }}>Subscription</Typography>
              <Paper sx={{ p: 2 }}>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Current Plan"
                      secondary={currentInstitution.plan?.toUpperCase()}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Status"
                      secondary={currentInstitution.subscription_status}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText
                      primary="Expiry Date"
                      secondary={currentInstitution.plan_expiry ? new Date(currentInstitution.plan_expiry).toLocaleDateString() : 'N/A'}
                    />
                  </ListItem>
                  <Divider />
                  <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                    <Button variant="contained">Upgrade Plan</Button>
                    <Button variant="outlined">View Billing History</Button>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Container>
  );
}
